/**
 * comfyOutputService.js
 * Service to handle ComfyOutput image operations
 */

import electronBridge from '../utils/electronBridge';
const ipcRenderer = electronBridge.ipcRenderer;

/**
 * Fetch output images with pagination and filtering
 * @param {number} page - Page number (-1 for all images)
 * @param {string} category - Filter category ("all", "image", "video", "audio")
 * @param {string} sortMethod - Sort method ("newest", "oldest")
 * @returns {Promise} - Promise that resolves with image data
 */
export const fetchOutputImages = (page = 1, category = "all", sortMethod = "newest") => {
  return new Promise((resolve) => {
    // Track all received items
    let allItems = [];
    
    // Register a one-time listener for the initial image batch
    const handleInitialImage = (event, imageData, finished) => {
      try {
        if (imageData && imageData !== "{}") {
          const parsedData = JSON.parse(imageData);
          
          // Add initial batch to our collection
          if (Array.isArray(parsedData)) {
            allItems = [...allItems, ...parsedData];
          } else {
            allItems.push(parsedData);
          }
          
          if (finished) {
            // If this was the only/last batch, resolve with all received items
            ipcRenderer.removeListener('comfy-output-initial', handleInitialImage);
            ipcRenderer.removeListener('comfy-output-stream', handleStreamData);
            resolve(allItems);
          }
        } else if (finished) {
          // Empty data but finished flag is true
          ipcRenderer.removeListener('comfy-output-initial', handleInitialImage);
          ipcRenderer.removeListener('comfy-output-stream', handleStreamData);
          resolve(allItems);
        }
      } catch (error) {
        console.error("Error parsing comfy-output-initial data:", error);
        ipcRenderer.removeListener('comfy-output-initial', handleInitialImage);
        ipcRenderer.removeListener('comfy-output-stream', handleStreamData);
        resolve(allItems);
      }
    };
    
    // Handle streaming batches
    const handleStreamData = (event, imageData, finished) => {
      try {
        if (imageData && imageData !== "{}") {
          const parsedData = JSON.parse(imageData);
          
          // Add batch to our collection
          if (Array.isArray(parsedData)) {
            allItems = [...allItems, ...parsedData];
          } else {
            allItems.push(parsedData);
          }
        }
        
        if (finished) {
          // Last batch received, resolve with all items
          ipcRenderer.removeListener('comfy-output-initial', handleInitialImage);
          ipcRenderer.removeListener('comfy-output-stream', handleStreamData);
          resolve(allItems);
        }
      } catch (error) {
        console.error("Error parsing comfy-output-stream data:", error);
        if (finished) {
          ipcRenderer.removeListener('comfy-output-initial', handleInitialImage);
          ipcRenderer.removeListener('comfy-output-stream', handleStreamData);
          resolve(allItems);
        }
      }
    };

    // Listen for batches
    ipcRenderer.once('comfy-output-initial', handleInitialImage);
    ipcRenderer.on('comfy-output-stream', handleStreamData);
    
    // Send the request to the main process
    ipcRenderer.send('fetch-comfy-output', page, category, sortMethod);
  });
};

/**
 * Get the output directory path
 * @returns {Promise<string>} - Promise that resolves with the output directory path
 */
export const getOutputDirectoryPath = async () => {
  try {
    return await ipcRenderer.invoke('get-comfy-output-path');
  } catch (error) {
    console.error("Error getting output directory path:", error);
    return null;
  }
};

/**
 * Fetch a full-size image by hash
 * @param {string} hash - Hash of the image to fetch
 * @returns {Promise<Object>} - Promise that resolves with the full-size image data
 */
export const fetchFullSizeImage = async (hash) => {
  try {
    const result = await ipcRenderer.invoke('fetch-full-image', hash);
    if (result.success) {
      return result.image;
    }
    return null;
  } catch (error) {
    console.error("Error fetching full-size image:", error);
    return null;
  }
};

/**
 * Listen for additional images streaming in after the initial image
 * @param {Function} callback - Callback function to handle incoming images
 * @returns {Function} - Function to remove the listener
 */
export const listenForOutputImages = (callback) => {
  const handleImageData = (event, imageData) => {
    try {
      if (imageData && imageData !== "{}") {
        const parsedData = JSON.parse(imageData);
        callback(parsedData);
      }
    } catch (error) {
      console.error("Error parsing comfy-output-stream data:", error);
    }
  };

  ipcRenderer.on('comfy-output-stream', handleImageData);
  
  // Return a cleanup function to remove the listener
  return () => {
    ipcRenderer.removeListener('comfy-output-stream', handleImageData);
  };
};

/**
 * Delete an image by hash
 * @param {string} imageHash - Hash of the image to delete
 * @returns {Promise<boolean>} - Promise that resolves with success status
 */
export const deleteOutputImage = async (imageHash) => {
  return new Promise((resolve) => {
    ipcRenderer.once('delete-image-result', (_, result) => {
      resolve(result.success);
    });
    
    ipcRenderer.send('delete-image', imageHash);
  });
};

/**
 * Update image metadata
 * @param {Object} updatedImage - Updated image data
 * @returns {Promise<boolean>} - Promise that resolves with success status
 */
export const updateOutputImage = async (updatedImage) => {
  return new Promise((resolve) => {
    ipcRenderer.once('update-image-result', (_, result) => {
      resolve(result.success);
    });
    
    ipcRenderer.send('update-image', updatedImage);
  });
};

/**
 * Rebuild the output database
 * @returns {Promise<Object>} - Promise that resolves with result
 */
export const rebuildOutputDatabase = async () => {
  return new Promise((resolve) => {
    ipcRenderer.invoke('rebuildOutputJSON').then(result => {
      resolve(result);
    });
  });
};

/**
 * Regenerate thumbnail for a specific media item
 * @param {string} hash - Hash of the media item
 * @returns {Promise<boolean>} - Promise that resolves with success status
 */
export const regenerateThumbnail = async (hash) => {
  try {
    const result = await ipcRenderer.invoke('regenerate-thumbnail', hash);
    return result.success;
  } catch (error) {
    console.error("Error regenerating thumbnail:", error);
    return false;
  }
};

/**
 * Regenerate thumbnails for all video files
 * @returns {Promise<object>} - Promise that resolves with results
 */
export const regenerateAllVideoThumbnails = async () => {
  try {
    console.log("Starting regeneration of all video thumbnails");
    const result = await ipcRenderer.invoke('regenerate-all-video-thumbnails');
    console.log("Result of regenerating all video thumbnails:", result);
    return result;
  } catch (error) {
    console.error("Error regenerating all video thumbnails:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get the total count of media files
 * @param {string} category - Filter category ("all", "image", "video", "audio")
 * @returns {Promise<number>} - Promise that resolves with total count
 */
export const getTotalMediaCount = (category = "all") => {
  console.log("Requesting total media count for category:", category);
  
  return new Promise((resolve) => {
    // Use a flag to track if we've resolved already
    let hasResolved = false;
    
    // Function to safely resolve only once
    const safeResolve = (count) => {
      if (!hasResolved) {
        hasResolved = true;
        console.log(`Resolving getTotalMediaCount with count: ${count}`);
        resolve(count);
      }
    };
    
    // Primary method: direct count request
    ipcRenderer.once('comfy-output-count', (event, count) => {
      console.log("Received comfy-output-count response:", count);
      if (count && count > 0) {
        safeResolve(count);
      } else {
        console.warn("Received zero or invalid count, waiting for fallback");
      }
    });
    
    // Add a timeout for the primary method
    const primaryTimeout = setTimeout(() => {
      console.warn("Timeout waiting for primary count method");
      ipcRenderer.removeAllListeners('comfy-output-count');
      // Don't resolve yet, let the fallback handle it
    }, 2000);
    
    // Fallback method: count all items
    console.log("Setting up fallback count method");
    let allItemsCount = 0;
    
    // Track if we're done receiving all items
    let receivedInitial = false;
    
    // Handle initial batch
    ipcRenderer.once('comfy-output-initial', (_, data, finished) => {
      receivedInitial = true;
      try {
        if (data) {
          const parsedData = JSON.parse(data);
          if (Array.isArray(parsedData)) {
            allItemsCount += parsedData.length;
          } else if (parsedData) {
            allItemsCount += 1;
          }
        }
        
        if (finished) {
          clearTimeout(primaryTimeout);
          clearTimeout(fallbackTimeout);
          console.log(`Fallback count complete: ${allItemsCount} items`);
          safeResolve(allItemsCount);
        }
      } catch (e) {
        console.error("Error parsing fallback count data:", e);
        if (finished) {
          clearTimeout(primaryTimeout);
          clearTimeout(fallbackTimeout);
          safeResolve(allItemsCount);
        }
      }
    });
    
    // Handle streaming batches
    ipcRenderer.on('comfy-output-stream', (_, data, finished) => {
      if (!receivedInitial) return; // Wait for initial batch
      
      try {
        if (data) {
          const parsedData = JSON.parse(data);
          if (Array.isArray(parsedData)) {
            allItemsCount += parsedData.length;
          } else if (parsedData) {
            allItemsCount += 1;
          }
        }
        
        if (finished) {
          clearTimeout(primaryTimeout);
          clearTimeout(fallbackTimeout);
          ipcRenderer.removeAllListeners('comfy-output-stream');
          console.log(`Fallback count complete after stream: ${allItemsCount} items`);
          safeResolve(allItemsCount);
        }
      } catch (e) {
        console.error("Error parsing fallback stream data:", e);
        if (finished) {
          clearTimeout(primaryTimeout);
          clearTimeout(fallbackTimeout);
          ipcRenderer.removeAllListeners('comfy-output-stream');
          safeResolve(allItemsCount);
        }
      }
    });
    
    // Final fallback timeout
    const fallbackTimeout = setTimeout(() => {
      if (!hasResolved) {
        console.warn("All count methods timed out, using best available count");
        ipcRenderer.removeAllListeners('comfy-output-count');
        ipcRenderer.removeAllListeners('comfy-output-initial');
        ipcRenderer.removeAllListeners('comfy-output-stream');
        // Use whatever count we have, or 0 if nothing
        safeResolve(allItemsCount || 0);
      }
    }, 8000);
    
    // Send the requests
    console.log("Sending fetch-comfy-output-count request");
    ipcRenderer.send('fetch-comfy-output-count', category);
    
    console.log("Sending fetch-comfy-output request for fallback count");
    ipcRenderer.send('fetch-comfy-output', -1, category, "newest");
  });
}; 